<template>
  <div class="p-0">
    <footer id="footer" class="text-center text-white py-3">
      <div class="mainWrap">
        <div class="row mt-3">
          <div class="col-12 col-lg-10 offset-lg-1">
            <div class="row">
              <div class="col-12 mb-3">
                <div class="d-flex justify-content-start">
                  <span class="footer_headline"
                    >Connect with us:
                    <StoreLink
                      :href="footerLinks.plcnextCommunity"
                      rel="noopener noreferrer"
                      target="_blank"
                      >PLCnext Community</StoreLink
                    ></span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row my-3 align-items-start">
          <div class="col-12 col-lg-3 offset-lg-1 col-md-6">
            <div class="row mt-4 mb-4">
              <div class="col-12">
                <div
                  class="d-flex flex-wrap justify-content-center justify-content-md-start gap-3"
                >
                  <StoreLink
                    v-for="(item, i) in items"
                    :key="i"
                    :href="footerLinks[item]"
                    class="text-white"
                    rel="noopener noreferrer"
                    target="_blank"
                    :aria-label="item"
                  >
                    <PhGithubLogo v-if="item === 'github'" size="32" />
                    <PhFacebookLogo v-if="item === 'facebook'" size="32" />
                    <PhYoutubeLogo v-if="item === 'youtube'" size="32" />
                    <PhLinkedinLogo v-if="item === 'linkedin'" size="32" />
                    <span class="visually-hidden">{{ item }}</span>
                  </StoreLink>
                </div>
              </div>
            </div>
          </div>

          <div
            class="col-12 col-md-6 col-lg-8 d-flex flex-column flex-lg-row flex-wrap justify-content-lg-around gap-5"
          >
            <div
              class="d-flex flex-column align-items-center align-items-md-start footer_links"
            >
              <span class="mt-4 footer_section_title">PLCNEXT STORE</span>

              <StoreLink class="mt-3" :href="'/' + hubName + '/companies'">
                Developers
              </StoreLink>

              <StoreLink href="/become-a-developer" class="mt-3">
                Become a developer
              </StoreLink>

              <StoreLink
                :href="headerLinks.manual"
                class="mt-3"
                rel="noopener noreferrer"
                target="_blank"
              >
                Store Info Center
              </StoreLink>
            </div>

            <div
              class="d-flex flex-column align-items-center align-items-md-start footer_links"
            >
              <span class="mt-4 footer_section_title">AREAS</span>

              <StoreLink
                v-for="hub in hubStore.hubs"
                :key="hub.name"
                :href="'/' + hub.name"
                class="mt-3"
              >
                {{ capitalise(hub.name) }} Store
              </StoreLink>
            </div>
          </div>
        </div>
      </div>
    </footer>

    <div id="second_footer" class="bg-white text-black py-3">
      <div class="mainWrap">
        <div class="row align-items-center g-4">
          <div class="col-12 col-lg-8 offset-lg-1 col-md-10">
            <div
              class="d-flex flex-wrap align-items-center justify-center gap-3"
            >
              <StoreLink to="/terms-conditions" class="fw-bold"
                >Terms & Conditions</StoreLink
              >

              <StoreLink
                v-if="isUsHub()"
                :href="footerLinks.siteNoticeUS"
                class="fw-bold"
                rel="noopener noreferrer"
                target="_blank"
                >Site notice</StoreLink
              >

              <StoreLink class="fw-bold pointer" @click="openPrivacySettings"
                >Privacy Settings</StoreLink
              >

              <StoreLink
                v-if="isEuHub() || isWorldHub()"
                :href="footerLinks.siteNoticeEUOrWorld"
                class="fw-bold"
                rel="noopener noreferrer"
                target="_blank"
                >Site notice</StoreLink
              >

              <StoreLink
                :href="footerLinks.dataPrivacy"
                class="fw-bold"
                rel="noopener noreferrer"
                target="_blank"
                >Data Privacy</StoreLink
              >

              <StoreLink :to="{ path: '/report' }" class="fw-bold"
                >Contact Us</StoreLink
              >
            </div>
          </div>
          <div class="col-12 col-lg-2 col-md-2 text-center text-lg-start">
            <img alt="PCLogo" src="@/assets/img/logos/PCLogo.gif" />
          </div>
        </div>
        <div class="row align-items-center g-4">
          <div class="col-12 col-lg-8 offset-lg-1 col-md-10">
            <span class="text-white">
              <span class="appsData__version"
                >Version:
                {{ deploymentInfoStore.deploymentInfo?.version }}</span
              >,
              <span class="appsData__bundle"
                >Build:
                {{ deploymentInfoStore.deploymentInfo?.buildNumber }}</span
              >
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useHubsStore } from "~/stores/hubsstore";
import { footerLinks, headerLinks } from "~~/public/constants";
import { useUserStore } from "~/stores/userstore";
import { useDeployementInfoStore } from "~/stores/deploymentinfostore";
import StoreLink from "~/components/shared/buttons/StoreLink.vue";
import { useToastStore } from "~/stores/ui/toaststore";
import {
  PhFacebookLogo,
  PhGithubLogo,
  PhLinkedinLogo,
  PhYoutubeLogo,
} from "@phosphor-icons/vue";

const hubStore = useHubsStore();

const items = [
  "github",
  "facebook",
  "youtube",
  "linkedin",
] as const satisfies ReadonlyArray<keyof typeof footerLinks>;

const userStore = useUserStore();
const deploymentInfoStore = useDeployementInfoStore();

const isUsHub = () => {
  if (userStore.userHub?.hub?.name) {
    return userStore.userHub.hub.name.toLowerCase() === "us";
  }
  return false;
};

const isEuHub = () => {
  if (userStore.userHub?.hub?.name) {
    return userStore.userHub.hub.name.toLowerCase() === "eu";
  }
  return false;
};

const isWorldHub = () => {
  if (userStore.userHub?.hub?.name) {
    return userStore.userHub.hub.name.toLowerCase() === "world";
  }
  return false;
};

// use route hubName if available otherwise userHub
const route = useRoute();
const routeHub = computed(() => {
  const h = route.params.hub as string | string[] | undefined;
  return Array.isArray(h) ? h[0] : h;
});

const hubName = computed(() => {
  if (routeHub.value) {
    return routeHub.value.toLowerCase();
  }
  return userStore.userHub?.hub?.name || "world";
});

const capitalise = (str: string | undefined | null) => {
  if (!str) return "";
  // if string is only 2 letters, capitalise it completely
  if (str.length === 2) {
    return str.toUpperCase();
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const openPrivacySettings = () => {
  // @ts-expect-error Usercentric script which is included in nuxt.config.ts
  const userCentricObject = window.UC_UI;
  if (userCentricObject) {
    userCentricObject.showSecondLayer();
  } else {
    useToastStore().addToastWithMessageAndTitle(
      "Could not load privacy settings",
      "Usercentrics script not loaded",
      "warning",
    );
  }
};
</script>
<style scoped lang="scss">
@use "@/sass/components/Footer.scss" as *;
</style>
